/**
 * Floating Action Button with glass-like transparency effects
 * Inspired by Apple's design principles and Nothing's aesthetic
 */

import React from 'react';
import { Pressable, ViewStyle, Animated } from 'react-native';
import { useTheme } from '../themes';
import { colorTokens } from '../tokens';

export interface FloatingActionButtonProps {
  children: React.ReactNode;
  onPress: () => void;
  variant?: 'glass' | 'solid' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  testID?: string;
  style?: ViewStyle;
}

export function FloatingActionButton({
  children,
  onPress,
  variant = 'glass',
  size = 'md',
  disabled = false,
  testID,
  style,
}: FloatingActionButtonProps) {
  const { theme } = useTheme();
  const scaleValue = React.useRef(new Animated.Value(1)).current;
  const opacityValue = React.useRef(new Animated.Value(1)).current;

  // Size configurations
  const sizeConfig = {
    sm: {
      width: theme.spacing.touchTargets.minimum,
      height: theme.spacing.touchTargets.minimum,
      borderRadius: theme.spacing.touchTargets.minimum / 2,
    },
    md: {
      width: theme.spacing.touchTargets.comfortable,
      height: theme.spacing.touchTargets.comfortable,
      borderRadius: theme.spacing.touchTargets.comfortable / 2,
    },
    lg: {
      width: theme.spacing.touchTargets.large,
      height: theme.spacing.touchTargets.large,
      borderRadius: theme.spacing.touchTargets.large / 2,
    },
  };

  // Get background style based on variant
  const getBackgroundStyle = () => {
    switch (variant) {
      case 'glass':
        return {
          backgroundColor: theme.colors.surface.overlay,
          backdropFilter: 'blur(20px)', // Web only
        };
      case 'solid':
        return {
          backgroundColor: colorTokens.primary[500],
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          borderWidth: 1,
          borderColor: theme.colors.border.primary,
        };
      default:
        return {
          backgroundColor: theme.colors.surface.overlay,
        };
    }
  };

  // Handle press animations
  const handlePressIn = () => {
    if (!disabled) {
      Animated.parallel([
        Animated.spring(scaleValue, {
          toValue: 0.95,
          useNativeDriver: true,
          tension: 300,
          friction: 20,
        }),
        Animated.timing(opacityValue, {
          toValue: 0.8,
          duration: theme.motion.duration.fast,
          useNativeDriver: true,
        }),
      ]).start();
    }
  };

  const handlePressOut = () => {
    if (!disabled) {
      Animated.parallel([
        Animated.spring(scaleValue, {
          toValue: 1,
          useNativeDriver: true,
          tension: 300,
          friction: 20,
        }),
        Animated.timing(opacityValue, {
          toValue: 1,
          duration: theme.motion.duration.fast,
          useNativeDriver: true,
        }),
      ]).start();
    }
  };

  const buttonStyle = {
    ...sizeConfig[size],
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    opacity: disabled ? 0.5 : 1,
    ...getBackgroundStyle(),
    ...theme.spacing.elevation[2], // Subtle shadow for floating effect
  };

  const animatedStyle = {
    transform: [{ scale: scaleValue }],
    opacity: opacityValue,
  };

  return (
    <Animated.View style={[buttonStyle, animatedStyle, style]}>
      <Pressable
        style={{
          width: '100%',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onPress={onPress}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        disabled={disabled}
        testID={testID}
        accessibilityRole="button"
        accessibilityState={{ disabled }}
      >
        {children}
      </Pressable>
    </Animated.View>
  );
}